import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import CustomDomainsSection from '../components/CustomDomainsSection';
import { toast } from 'sonner';
import { 
  User, 
  Mail, 
  Lock, 
  Shield, 
  Bell, 
  CreditCard, 
  LogOut,
  ArrowLeft,
  Check,
  AlertCircle,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  UserPlus,
  Users,
  Palette,
  Upload,
  Image,
  X,
  Trash2,
  Copy,
  UserMinus,
  Clock,
  Crown,
  ChevronDown,
  History
} from 'lucide-react';

const SettingsPage = () => {
  const { user, logout, updateProfile, updateEmail, changePassword, requestOtp, verifyEmail, api, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  // Profile state
  const [name, setName] = useState(user?.name || '');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  
  // Email change state
  const [newEmail, setNewEmail] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [isEmailOtpSent, setIsEmailOtpSent] = useState(false);
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordOtp, setPasswordOtp] = useState('');
  const [isPasswordOtpSent, setIsPasswordOtpSent] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  
  // Email verification state
  const [verifyOtp, setVerifyOtp] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerifyOtpSent, setIsVerifyOtpSent] = useState(false);

  // Grant access state
  const [grantEmail, setGrantEmail] = useState('');
  const [grantPermission, setGrantPermission] = useState('viewer');
  const [isGranting, setIsGranting] = useState(false);
  const [lastGrantResult, setLastGrantResult] = useState(null);
  const currentWorkspaceId = localStorage.getItem('currentWorkspaceId');

  // Team members state
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState(null);
  const [cancellingInviteEmail, setCancellingInviteEmail] = useState(null);
  const [isWorkspaceAdmin, setIsWorkspaceAdmin] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [updatingPermission, setUpdatingPermission] = useState(false);

  // Branding state
  const [branding, setBranding] = useState({
    business_name: '',
    logo_data: '',
    plan: 'launchpad',
    whitelabel_enabled: false
  });
  const [isSavingBranding, setIsSavingBranding] = useState(false);
  const [isLoadingBranding, setIsLoadingBranding] = useState(true);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Fetch branding on mount
  React.useEffect(() => {
    if (currentWorkspaceId) {
      fetchBranding();
      fetchMembers();
      fetchAccessLog();
    }
  }, [currentWorkspaceId]);

  const fetchBranding = async () => {
    try {
      const response = await api.get(`/api/workspaces/${currentWorkspaceId}/branding`);
      setBranding(response.data);
      if (response.data.logo_data) {
        setLogoPreview(response.data.logo_data);
      }
    } catch (err) {
      console.error('Failed to fetch branding:', err);
    } finally {
      setIsLoadingBranding(false);
    }
  };

  const fetchMembers = async () => {
    if (!currentWorkspaceId) return;
    setIsLoadingMembers(true);
    try {
      const response = await api.get(`/api/workspaces/${currentWorkspaceId}/members`);
      const membersList = response.data.members || [];
      setMembers(membersList);
      setPendingInvites(response.data.pending_invites || []);
      
      // Check if current user is a workspace admin or owner
      const currentMember = membersList.find(m => m.user_id === user?.user_id);
      const memberPerm = currentMember?.permission_level;
      const memberIsOwner = currentMember?.is_owner;
      setIsWorkspaceAdmin(isAdmin || memberIsOwner || memberPerm === 'admin' || memberPerm === 'owner');
    } catch (err) {
      console.error('Failed to fetch members:', err);
    } finally {
      setIsLoadingMembers(false);
    }
  };

  const [confirmRemoveId, setConfirmRemoveId] = useState(null);

  // Recent access activity (audit log)
  const [accessLog, setAccessLog] = useState([]);
  const [isLoadingLog, setIsLoadingLog] = useState(false);

  const fetchAccessLog = async () => {
    if (!currentWorkspaceId) return;
    setIsLoadingLog(true);
    try {
      const response = await api.get(`/api/workspaces/${currentWorkspaceId}/access-log?limit=15`);
      setAccessLog(response.data.events || []);
    } catch (err) {
      // Non-fatal — log panel just stays empty
      console.error('Failed to fetch access log:', err);
    } finally {
      setIsLoadingLog(false);
    }
  };

  const handleRemoveMember = async (userId, memberName) => {
    if (confirmRemoveId !== userId) {
      setConfirmRemoveId(userId);
      return;
    }
    setRemovingMemberId(userId);
    try {
      await api.delete(`/api/workspaces/${currentWorkspaceId}/members/${userId}`);
      toast.success(`${memberName} has been removed`);
      setConfirmRemoveId(null);
      fetchMembers();
      fetchAccessLog();
    } catch (err) {
      console.error('Remove member error:', err);
      toast.error(err.response?.data?.detail || 'Failed to remove member');
    } finally {
      setRemovingMemberId(null);
    }
  };

  const handleCancelInvite = async (email) => {
    setCancellingInviteEmail(email);
    try {
      await api.delete(`/api/workspaces/${currentWorkspaceId}/invites/${encodeURIComponent(email)}`);
      toast.success(`Invite cancelled for ${email}`);
      fetchMembers();
      fetchAccessLog();
    } catch (err) {
      console.error('Cancel invite error:', err);
      toast.error(err.response?.data?.detail || 'Failed to cancel invite');
    } finally {
      setCancellingInviteEmail(null);
    }
  };

  const handleUpdatePermission = async (userId, newPermission) => {
    setUpdatingPermission(true);
    try {
      await api.patch(`/api/workspaces/${currentWorkspaceId}/members/${userId}`, {
        permission_level: newPermission
      });
      toast.success('Permission updated');
      setEditingMemberId(null);
      fetchMembers();
      fetchAccessLog();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update permission');
    } finally {
      setUpdatingPermission(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Profile handlers
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setIsSavingProfile(true);
    try {
      await updateProfile({ name });
      toast.success('Profile updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Email change handlers
  const handleRequestEmailOtp = async () => {
    if (!newEmail) {
      toast.error('Please enter a new email address');
      return;
    }
    setIsSavingEmail(true);
    try {
      await requestOtp(newEmail, 'email_change');
      setIsEmailOtpSent(true);
      toast.success('Verification code sent to new email');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setIsSavingEmail(false);
    }
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    if (!emailOtp) {
      toast.error('Please enter the verification code');
      return;
    }
    setIsSavingEmail(true);
    try {
      await updateEmail(newEmail, emailOtp);
      toast.success('Email updated successfully');
      setNewEmail('');
      setEmailOtp('');
      setIsEmailOtpSent(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update email');
    } finally {
      setIsSavingEmail(false);
    }
  };

  // Password change handlers
  const handleRequestPasswordOtp = async () => {
    setIsSavingPassword(true);
    try {
      await requestOtp(user.email, 'password_change');
      setIsPasswordOtpSent(true);
      toast.success('Verification code sent to your email');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setIsSavingPassword(true);
    try {
      await changePassword(currentPassword, newPassword, passwordOtp || null);
      toast.success('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordOtp('');
      setIsPasswordOtpSent(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update password');
    } finally {
      setIsSavingPassword(false);
    }
  };

  // Email verification handlers
  const handleRequestVerifyOtp = async () => {
    setIsVerifying(true);
    try {
      await requestOtp(user.email, 'verify_email');
      setIsVerifyOtpSent(true);
      toast.success('Verification code sent to your email');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setIsVerifying(true);
    try {
      await verifyEmail(verifyOtp);
      toast.success('Email verified successfully');
      setVerifyOtp('');
      setIsVerifyOtpSent(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to verify email');
    } finally {
      setIsVerifying(false);
    }
  };

  // Grant access handler
  const handleGrantAccess = async (e) => {
    e.preventDefault();
    if (!grantEmail || !currentWorkspaceId) {
      toast.error('Please enter an email address');
      return;
    }
    setIsGranting(true);
    setLastGrantResult(null);
    try {
      const response = await api.post('/api/access/grant', {
        email: grantEmail,
        workspace_id: currentWorkspaceId,
        permission_level: grantPermission
      });
      toast.success(response.data.message);
      
      // Show temp password if new user was created
      if (response.data.is_new_user && response.data.temp_password) {
        setLastGrantResult({
          email: grantEmail,
          tempPassword: response.data.temp_password,
          isNewUser: true
        });
      } else {
        setLastGrantResult({
          email: grantEmail,
          isNewUser: false
        });
      }
      
      setGrantEmail('');
      fetchMembers();
      fetchAccessLog();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to grant access');
    } finally {
      setIsGranting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // Render a relative timestamp like "3m ago", "2h ago", "Mar 12"
  const formatRelativeTime = (iso) => {
    if (!iso) return '';
    const then = new Date(iso);
    if (Number.isNaN(then.getTime())) return '';
    const diffSec = Math.max(0, Math.floor((Date.now() - then.getTime()) / 1000));
    if (diffSec < 45) return 'just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    if (diffSec < 7 * 86400) return `${Math.floor(diffSec / 86400)}d ago`;
    return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const ACCESS_EVENT_STYLE = {
    access:           { color: 'text-violet-300', dot: 'bg-violet-400',  label: 'Granted' },
    invite:           { color: 'text-teal-300',   dot: 'bg-teal-400',    label: 'Invited' },
    permission_change:{ color: 'text-amber-300',  dot: 'bg-amber-400',   label: 'Permission' },
    access_revoked:   { color: 'text-red-300',    dot: 'bg-red-400',     label: 'Removed' },
    invite_cancelled: { color: 'text-gray-300',   dot: 'bg-gray-400',    label: 'Cancelled' },
  };

  // Branding handlers
  const handleSaveBranding = async (e) => {
    e.preventDefault();
    setIsSavingBranding(true);
    try {
      const response = await api.put(`/api/workspaces/${currentWorkspaceId}/branding`, {
        business_name: branding.business_name,
        logo_data: branding.logo_data || null
      });
      setBranding(response.data);
      toast.success('Branding updated successfully');
      // Reload the page to reflect changes in sidebar
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update branding');
    } finally {
      setIsSavingBranding(false);
    }
  };

  const handleLogoUpload = (file) => {
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a PNG, JPG, or SVG file');
      return;
    }
    
    // Validate file size (max 500KB)
    if (file.size > 500 * 1024) {
      toast.error('Logo must be smaller than 500KB');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      setBranding({ ...branding, logo_data: base64 });
      setLogoPreview(base64);
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleLogoUpload(file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    handleLogoUpload(file);
  };

  const handleRemoveLogo = () => {
    setBranding({ ...branding, logo_data: '' });
    setLogoPreview(null);
  };

  const [activeSection, setActiveSection] = useState('account');

  const sections = [
    { id: 'account', icon: User, label: 'Account' },
    { id: 'security', icon: Lock, label: 'Security' },
    { id: 'team', icon: Users, label: 'Team & Access' },
    ...(currentWorkspaceId ? [{ id: 'branding', icon: Palette, label: 'Branding' }] : []),
  ];

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="settings-page">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <h1 className="text-2xl font-semibold text-white">Settings</h1>
          <p className="text-sm text-gray-400">Manage your account settings and preferences</p>
        </motion.div>

        {/* Email Verification Banner */}
        {!user?.email_verified && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-xl p-4 border-yellow-500/30 bg-yellow-500/5 mb-6"
          >
            <div className="flex items-start gap-4">
              <AlertCircle className="text-yellow-400 flex-shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <h3 className="font-medium text-yellow-400 mb-1">Verify your email</h3>
                <p className="text-sm text-gray-400 mb-3">Please verify your email address to access all features.</p>
                {!isVerifyOtpSent ? (
                  <button onClick={handleRequestVerifyOtp} disabled={isVerifying} className="px-4 py-2 rounded-lg bg-yellow-400 text-space-900 text-sm font-medium hover:bg-yellow-500 disabled:opacity-50 transition-colors">
                    {isVerifying ? <Loader2 className="animate-spin" size={16} /> : 'Send verification code'}
                  </button>
                ) : (
                  <form onSubmit={handleVerifyEmail} className="flex items-center gap-2">
                    <input type="text" value={verifyOtp} onChange={(e) => setVerifyOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Enter 6-digit code" className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-500 focus:border-yellow-400/50 focus:outline-none w-40" maxLength={6} />
                    <button type="submit" disabled={isVerifying || verifyOtp.length !== 6} className="px-4 py-2 rounded-lg bg-yellow-400 text-space-900 text-sm font-medium hover:bg-yellow-500 disabled:opacity-50 transition-colors">
                      {isVerifying ? <Loader2 className="animate-spin" size={16} /> : 'Verify'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </motion.div>
        )}

        <div className="flex gap-6">
          {/* Left Sidebar */}
          <div className="w-48 shrink-0 hidden md:block">
            <nav className="space-y-1 sticky top-20">
              {sections.map((sec) => (
                <button
                  key={sec.id}
                  onClick={() => setActiveSection(sec.id)}
                  data-testid={`settings-nav-${sec.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    activeSection === sec.id
                      ? 'bg-white/10 text-white'
                      : 'text-gray-400 hover:bg-white/5'
                  }`}
                >
                  <sec.icon size={16} className={activeSection === sec.id ? 'text-teal-400' : ''} />
                  {sec.label}
                </button>
              ))}
              <div className="pt-3 mt-3 border-t border-white/10">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-red-400 hover:bg-red-400/10 transition-all"
                  data-testid="settings-logout-btn"
                >
                  <LogOut size={16} /> Sign Out
                </button>
              </div>
            </nav>
          </div>

          {/* Right Content */}
          <div className="flex-1 min-w-0 space-y-6">
            {/* ACCOUNT SECTION */}
            {activeSection === 'account' && (
              <motion.div key="account" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                {/* Profile */}
                <div className="glass-card rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 rounded-lg bg-teal-400/10 flex items-center justify-center">
                      <User className="text-teal-400" size={18} />
                    </div>
                    <div>
                      <h2 className="text-sm font-semibold text-white">Profile</h2>
                      <p className="text-xs text-gray-400">Update your personal information</p>
                    </div>
                  </div>
                  <form onSubmit={handleSaveProfile} className="space-y-4">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5">Full Name</label>
                      <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-teal-400/50 focus:outline-none text-sm" data-testid="settings-name-input" />
                    </div>
                    <button type="submit" disabled={isSavingProfile || name === user?.name} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-teal-400 text-space-900 text-sm font-medium hover:bg-teal-500 disabled:opacity-50 transition-colors" data-testid="settings-save-profile-btn">
                      {isSavingProfile ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />}
                      Save
                    </button>
                  </form>
                </div>

                {/* Email */}
                <div className="glass-card rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 rounded-lg bg-violet-400/10 flex items-center justify-center">
                      <Mail className="text-violet-400" size={18} />
                    </div>
                    <div>
                      <h2 className="text-sm font-semibold text-white">Email Address</h2>
                      <p className="text-xs text-gray-400">Change your email address</p>
                    </div>
                  </div>
                  <div className="mb-4 p-3 rounded-lg bg-white/5">
                    <p className="text-xs text-gray-400">Current email</p>
                    <p className="text-sm text-white font-medium flex items-center gap-2">
                      {user?.email}
                      {user?.email_verified && <span className="px-2 py-0.5 rounded-full bg-green-400/10 text-green-400 text-[10px]">Verified</span>}
                    </p>
                  </div>
                  <form onSubmit={handleUpdateEmail} className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5">New Email Address</label>
                      <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="Enter new email" className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-violet-400/50 focus:outline-none text-sm" disabled={isEmailOtpSent} data-testid="settings-new-email-input" />
                    </div>
                    {isEmailOtpSent && (
                      <div>
                        <label className="block text-xs text-gray-400 mb-1.5">Verification Code</label>
                        <div className="relative">
                          <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                          <input type="text" value={emailOtp} onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Enter 6-digit code" className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-violet-400/50 focus:outline-none text-sm" maxLength={6} data-testid="settings-email-otp-input" />
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-3">
                      {!isEmailOtpSent ? (
                        <button type="button" onClick={handleRequestEmailOtp} disabled={isSavingEmail || !newEmail} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-400 text-white text-sm font-medium hover:bg-violet-500 disabled:opacity-50 transition-colors" data-testid="settings-request-email-otp-btn">
                          {isSavingEmail ? <Loader2 className="animate-spin" size={16} /> : <Mail size={16} />}
                          Send Verification Code
                        </button>
                      ) : (
                        <>
                          <button type="submit" disabled={isSavingEmail || emailOtp.length !== 6} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-400 text-white text-sm font-medium hover:bg-violet-500 disabled:opacity-50 transition-colors" data-testid="settings-update-email-btn">
                            {isSavingEmail ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />}
                            Update Email
                          </button>
                          <button type="button" onClick={() => { setIsEmailOtpSent(false); setEmailOtp(''); }} className="px-4 py-2.5 rounded-xl text-gray-400 hover:text-white text-sm transition-colors">Cancel</button>
                        </>
                      )}
                    </div>
                  </form>
                </div>
              </motion.div>
            )}

            {/* SECURITY SECTION */}
            {activeSection === 'security' && (
              <motion.div key="security" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="glass-card rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 rounded-lg bg-phosphor-400/10 flex items-center justify-center">
                      <Lock className="text-phosphor-400" size={18} />
                    </div>
                    <div>
                      <h2 className="text-sm font-semibold text-white">Password</h2>
                      <p className="text-xs text-gray-400">Update your password for security</p>
                    </div>
                  </div>
                  <form onSubmit={handleChangePassword} className="space-y-4">
                    {user?.password !== null && (
                      <div>
                        <label className="block text-xs text-gray-400 mb-1.5">Current Password</label>
                        <div className="relative">
                          <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                          <input type={showCurrentPassword ? "text" : "password"} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Enter current password" className="w-full pl-10 pr-12 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-phosphor-400/50 focus:outline-none text-sm" data-testid="settings-current-password-input" />
                          <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                            {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                      </div>
                    )}
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5">New Password</label>
                      <div className="relative">
                        <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                        <input type={showNewPassword ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter new password (min. 8 characters)" className="w-full pl-10 pr-12 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-phosphor-400/50 focus:outline-none text-sm" minLength={8} data-testid="settings-new-password-input" />
                        <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                          {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5">Confirm New Password</label>
                      <div className="relative">
                        <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                        <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-phosphor-400/50 focus:outline-none text-sm" minLength={8} data-testid="settings-confirm-password-input" />
                      </div>
                    </div>
                    <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Shield size={14} className="text-phosphor-400" />
                        <span className="text-xs font-medium text-white">Extra Security (Optional)</span>
                      </div>
                      <p className="text-[11px] text-gray-400 mb-2">Add OTP verification for additional security when changing password.</p>
                      {!isPasswordOtpSent ? (
                        <button type="button" onClick={handleRequestPasswordOtp} disabled={isSavingPassword} className="text-xs text-phosphor-400 hover:text-phosphor-300 transition-colors">Request verification code</button>
                      ) : (
                        <div className="flex items-center gap-2">
                          <input type="text" value={passwordOtp} onChange={(e) => setPasswordOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Enter 6-digit code" className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white text-xs placeholder-gray-500 focus:border-phosphor-400/50 focus:outline-none w-36" maxLength={6} />
                          <button type="button" onClick={() => { setIsPasswordOtpSent(false); setPasswordOtp(''); }} className="text-xs text-gray-400 hover:text-white">Cancel</button>
                        </div>
                      )}
                    </div>
                    <button type="submit" disabled={isSavingPassword || !newPassword || !confirmPassword} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-phosphor-400 text-space-900 text-sm font-medium hover:bg-phosphor-500 disabled:opacity-50 transition-colors" data-testid="settings-update-password-btn">
                      {isSavingPassword ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />}
                      Update Password
                    </button>
                  </form>
                </div>
              </motion.div>
            )}

            {/* TEAM SECTION */}
            {activeSection === 'team' && currentWorkspaceId && (
              <motion.div key="team" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="glass-card rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 rounded-lg bg-violet-400/10 flex items-center justify-center">
                      <Users className="text-violet-400" size={18} />
                    </div>
                    <div>
                      <h2 className="text-sm font-semibold text-white">Team & Access</h2>
                      <p className="text-xs text-gray-400">Manage workspace members and invitations</p>
                    </div>
                  </div>

                  {/* Current Members */}
                  <div className="mb-6">
                    <h3 className="text-xs font-medium text-gray-300 mb-3 flex items-center gap-2 uppercase tracking-wider">
                      <Users size={12} /> Members ({members.length})
                    </h3>
                    {isLoadingMembers ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="animate-spin text-gray-400" size={20} />
                      </div>
                    ) : members.length === 0 ? (
                      <p className="text-sm text-gray-500 py-3">No members found</p>
                    ) : (
                      <div className="space-y-2">
                        {members.map((member) => (
                          <div key={member.user_id} className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/5 hover:border-white/10 transition-colors" data-testid={`member-row-${member.user_id}`}>
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400/20 to-violet-400/20 flex items-center justify-center flex-shrink-0">
                              {member.picture ? (
                                <img src={member.picture} alt="" className="w-8 h-8 rounded-full" />
                              ) : (
                                <span className="text-xs font-medium text-white">{member.name?.charAt(0)?.toUpperCase() || '?'}</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-white truncate">{member.name}</p>
                                {member.user_id === user?.user_id && <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-400/10 text-teal-400">You</span>}
                              </div>
                              <p className="text-xs text-gray-500 truncate">{member.email}</p>
                            </div>
                            
                            {isWorkspaceAdmin && member.user_id !== user?.user_id && !member.is_owner ? (
                              editingMemberId === member.user_id ? (
                                <select value={member.permission_level} onChange={(e) => handleUpdatePermission(member.user_id, e.target.value)} disabled={updatingPermission} className="text-xs px-2 py-1.5 rounded-lg bg-white/10 border border-white/10 text-white focus:outline-none focus:border-teal-400/50" data-testid={`edit-permission-${member.user_id}`} autoFocus onBlur={() => setEditingMemberId(null)}>
                                  <option value="viewer">Viewer</option>
                                  <option value="moderator">Moderator</option>
                                  <option value="admin">Admin</option>
                                </select>
                              ) : (
                                <button onClick={() => setEditingMemberId(member.user_id)} className={`text-xs px-2 py-1 rounded-full capitalize cursor-pointer hover:ring-1 hover:ring-white/20 transition-all ${member.permission_level === 'admin' ? 'bg-amber-400/10 text-amber-400' : member.permission_level === 'moderator' ? 'bg-blue-400/10 text-blue-400' : 'bg-gray-400/10 text-gray-400'}`} title="Click to change permission" data-testid={`permission-badge-${member.user_id}`}>
                                  {member.permission_level === 'admin' && <Crown size={10} className="inline mr-1" />}
                                  {member.permission_level}
                                  <ChevronDown size={10} className="inline ml-1 opacity-50" />
                                </button>
                              )
                            ) : (
                              <span className={`text-xs px-2 py-1 rounded-full capitalize ${member.is_owner || member.permission_level === 'owner' ? 'bg-amber-400/10 text-amber-400' : member.permission_level === 'admin' ? 'bg-amber-400/10 text-amber-400' : member.permission_level === 'moderator' ? 'bg-blue-400/10 text-blue-400' : 'bg-gray-400/10 text-gray-400'}`}>
                                {(member.is_owner || member.permission_level === 'owner' || member.permission_level === 'admin') && <Crown size={10} className="inline mr-1" />}
                                {member.is_owner ? 'Owner' : member.permission_level}
                              </span>
                            )}
                            
                            {member.user_id !== user?.user_id && !member.is_owner && isWorkspaceAdmin && (
                              <button onClick={(e) => { e.stopPropagation(); handleRemoveMember(member.user_id, member.name); }} disabled={removingMemberId === member.user_id} className={`px-2 py-1 rounded-lg text-xs font-medium transition-all ${confirmRemoveId === member.user_id ? 'bg-red-500 text-white hover:bg-red-600' : 'text-gray-500 hover:text-red-400 hover:bg-red-400/10'}`} title={confirmRemoveId === member.user_id ? 'Click again to confirm' : 'Remove member'} data-testid={`remove-member-${member.user_id}`}>
                                {removingMemberId === member.user_id ? <Loader2 className="animate-spin" size={14} /> : confirmRemoveId === member.user_id ? 'Remove?' : <Trash2 size={14} />}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Pending Invites */}
                  {pendingInvites.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-xs font-medium text-gray-300 mb-3 flex items-center gap-2 uppercase tracking-wider">
                        <Clock size={12} /> Pending Invites ({pendingInvites.length})
                      </h3>
                      <div className="space-y-2">
                        {pendingInvites.map((invite) => (
                          <div key={invite.email} className="flex items-center gap-3 p-3 rounded-lg bg-amber-400/5 border border-amber-400/10" data-testid={`pending-invite-${invite.email}`}>
                            <div className="w-8 h-8 rounded-full bg-amber-400/10 flex items-center justify-center flex-shrink-0">
                              <Mail size={12} className="text-amber-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate">{invite.email}</p>
                              <p className="text-xs text-gray-500">Invited {invite.invited_at ? new Date(invite.invited_at).toLocaleDateString() : 'recently'}</p>
                            </div>
                            <span className="text-xs px-2 py-1 rounded-full bg-amber-400/10 text-amber-400 capitalize">{invite.permission_level}</span>
                            {isWorkspaceAdmin && (
                              <button onClick={() => handleCancelInvite(invite.email)} disabled={cancellingInviteEmail === invite.email} className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-400/10 transition-colors" data-testid={`cancel-invite-${invite.email}`}>
                                {cancellingInviteEmail === invite.email ? <Loader2 className="animate-spin" size={14} /> : <X size={14} />}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent Access Activity (audit log) */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xs font-medium text-gray-300 flex items-center gap-2 uppercase tracking-wider">
                        <History size={12} /> Recent Activity
                      </h3>
                      <button
                        type="button"
                        onClick={fetchAccessLog}
                        disabled={isLoadingLog}
                        className="text-[11px] text-gray-500 hover:text-gray-300 transition-colors flex items-center gap-1"
                        data-testid="access-log-refresh-btn"
                      >
                        {isLoadingLog ? <Loader2 className="animate-spin" size={10} /> : null}
                        Refresh
                      </button>
                    </div>
                    {isLoadingLog && accessLog.length === 0 ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="animate-spin text-gray-400" size={18} />
                      </div>
                    ) : accessLog.length === 0 ? (
                      <p className="text-xs text-gray-500 py-3" data-testid="access-log-empty">
                        No access changes yet. Grants, removals, and permission changes will appear here.
                      </p>
                    ) : (
                      <div className="space-y-1.5" data-testid="access-log-list">
                        {accessLog.map((event, idx) => {
                          const style = ACCESS_EVENT_STYLE[event.type] || ACCESS_EVENT_STYLE.access;
                          return (
                            <div
                              key={`${event.time}-${idx}`}
                              className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/5 hover:border-white/10 transition-colors"
                              data-testid={`access-log-row-${idx}`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${style.dot}`} />
                              <span className={`text-[10px] font-medium uppercase tracking-wider w-20 flex-shrink-0 ${style.color}`}>
                                {style.label}
                              </span>
                              <p className="text-xs text-gray-300 flex-1 min-w-0 truncate">
                                {event.msg}
                                {event.actor_email && (
                                  <span className="text-gray-500"> · by {event.actor_email}</span>
                                )}
                              </p>
                              <span className="text-[10px] text-gray-500 flex-shrink-0" title={event.time}>
                                {formatRelativeTime(event.time)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Grant Access Form */}
                  {isWorkspaceAdmin && (
                    <div className="pt-4 border-t border-white/10">
                      <h3 className="text-xs font-medium text-gray-300 mb-3 flex items-center gap-2 uppercase tracking-wider">
                        <UserPlus size={12} /> Invite New Member
                      </h3>
                      {lastGrantResult && (
                        <div className={`mb-4 p-3 rounded-lg border ${lastGrantResult.isNewUser ? 'bg-teal-400/5 border-teal-400/20' : 'bg-blue-400/5 border-blue-400/20'}`} data-testid="grant-result-banner">
                          {lastGrantResult.isNewUser ? (
                            <div>
                              <p className="text-sm text-teal-300 font-medium mb-2">Account created for {lastGrantResult.email}</p>
                              <div className="flex items-center gap-2 p-2 rounded bg-space-900/50">
                                <span className="text-xs text-gray-400">Temp Password:</span>
                                <code className="text-sm text-teal-400 font-mono">{lastGrantResult.tempPassword}</code>
                                <button onClick={() => copyToClipboard(lastGrantResult.tempPassword)} className="p-1 rounded hover:bg-white/10 text-gray-400" data-testid="copy-temp-password-btn"><Copy size={12} /></button>
                              </div>
                              <p className="text-xs text-gray-500 mt-2">Share these credentials securely.</p>
                            </div>
                          ) : (
                            <p className="text-sm text-blue-300">Access granted to {lastGrantResult.email} (existing user).</p>
                          )}
                          <button onClick={() => setLastGrantResult(null)} className="mt-2 text-xs text-gray-500 hover:text-gray-300">Dismiss</button>
                        </div>
                      )}
                      <form onSubmit={handleGrantAccess} className="space-y-3">
                        <div className="flex gap-3">
                          <div className="flex-1 relative">
                            <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                            <input type="email" value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)} placeholder="team@example.com" className="w-full pl-9 pr-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-500 focus:border-violet-400/50 focus:outline-none" data-testid="grant-access-email-input" />
                          </div>
                          <select value={grantPermission} onChange={(e) => setGrantPermission(e.target.value)} className="px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none" data-testid="grant-access-permission-select">
                            <option value="viewer">Viewer</option>
                            <option value="moderator">Moderator</option>
                            <option value="admin">Admin</option>
                          </select>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-[11px] text-gray-500">New users get a temporary password. Existing users get instant access.</p>
                          <button type="submit" disabled={isGranting || !grantEmail} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-400 text-white text-sm font-medium hover:bg-violet-500 disabled:opacity-50 transition-colors" data-testid="grant-access-btn">
                            {isGranting ? <Loader2 className="animate-spin" size={14} /> : <UserPlus size={14} />}
                            Invite
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* BRANDING SECTION */}
            {activeSection === 'branding' && currentWorkspaceId && (
              <motion.div key="branding" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="glass-card rounded-xl p-6" data-testid="branding-section">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 rounded-lg bg-phosphor-400/10 flex items-center justify-center">
                      <Palette className="text-phosphor-400" size={18} />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-sm font-semibold text-white">Whitelabel Branding</h2>
                      <p className="text-xs text-gray-400">Customize how your workspace appears</p>
                    </div>
                    {!branding.whitelabel_enabled && (
                      <span className="px-2.5 py-1 rounded-full bg-violet-400/10 text-violet-400 text-xs font-medium" data-testid="branding-plan-badge">Logo & Domains: Agency/Pro</span>
                    )}
                    {branding.whitelabel_enabled && branding.is_lifetime && (
                      <span className="px-2.5 py-1 rounded-full bg-amber-400/10 text-amber-400 text-xs font-medium flex items-center gap-1" data-testid="branding-lifetime-badge">
                        <Crown size={11} /> LIFETIME
                      </span>
                    )}
                  </div>

                  {isLoadingBranding ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="animate-spin text-gray-400" size={24} />
                    </div>
                  ) : (
                    <form onSubmit={handleSaveBranding} className="space-y-5">
                      {/* Business Name — always editable */}
                      <div>
                        <label className="block text-xs text-gray-400 mb-1.5">Business Name</label>
                        <input
                          type="text"
                          value={branding.business_name}
                          onChange={(e) => setBranding({ ...branding, business_name: e.target.value })}
                          placeholder="Your Agency Name"
                          className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-phosphor-400/50 focus:outline-none text-sm"
                          data-testid="branding-business-name-input"
                        />
                        <p className="text-[11px] text-gray-500 mt-1.5">Appears in the workspace switcher and admin panel.</p>
                      </div>

                      {branding.whitelabel_enabled ? (
                        <>
                          {/* Custom Logo — Agency/Pro/Lifetime only */}
                          <div>
                            <label className="block text-xs text-gray-400 mb-1.5">Custom Logo</label>
                            {logoPreview && (
                              <div className="flex items-center gap-4 mb-3 p-3 rounded-xl bg-white/5 border border-white/10">
                                <div className="w-16 h-16 rounded-xl bg-white/10 flex items-center justify-center overflow-hidden p-1.5">
                                  <img src={logoPreview} alt="Logo preview" className="max-w-full max-h-full object-contain" onError={() => setLogoPreview(null)} />
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm text-white mb-0.5">Current Logo</p>
                                  <p className="text-[11px] text-gray-500">Click below to replace</p>
                                </div>
                                <button type="button" onClick={handleRemoveLogo} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-red-400 hover:bg-red-400/10 transition-colors border border-red-400/20 text-xs" data-testid="remove-logo-btn">
                                  <Trash2 size={12} /> Remove
                                </button>
                              </div>
                            )}
                            <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${isDragging ? 'border-phosphor-400 bg-phosphor-400/10' : 'border-white/20 hover:border-white/40 bg-white/5'}`} onClick={() => document.getElementById('logo-upload').click()} data-testid="logo-dropzone">
                              <input id="logo-upload" type="file" accept=".png,.jpg,.jpeg,.svg" onChange={handleFileSelect} className="hidden" />
                              <Upload className={`mx-auto mb-2 ${isDragging ? 'text-phosphor-400' : 'text-gray-500'}`} size={24} />
                              <p className={`text-xs mb-0.5 ${isDragging ? 'text-phosphor-400' : 'text-white'}`}>{isDragging ? 'Drop your logo here' : 'Drag & drop your logo here'}</p>
                              <p className="text-[11px] text-gray-500">or click to browse - PNG, JPG, SVG - Max 500KB</p>
                            </div>
                          </div>

                          {/* Preview */}
                          <div className="p-3 rounded-xl bg-space-800/50 border border-white/10">
                            <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-2">Preview</p>
                            <div className="flex items-center gap-3 p-2 rounded-lg bg-white/5">
                              {logoPreview ? (
                                <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center overflow-hidden"><img src={logoPreview} alt="" className="w-full h-full object-contain" /></div>
                              ) : (
                                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-teal-400 to-phosphor-400 flex items-center justify-center"><span className="text-space-900 font-bold text-xs">V</span></div>
                              )}
                              <span className="font-semibold text-sm text-white">{branding.business_name || <>Voxinity<span className="text-teal-400">AI</span></>}</span>
                            </div>
                          </div>

                          <button type="submit" disabled={isSavingBranding} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-phosphor-400 text-space-900 text-sm font-medium hover:bg-phosphor-500 disabled:opacity-50 transition-colors" data-testid="settings-save-branding-btn">
                            {isSavingBranding ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />}
                            Save Branding
                          </button>
                        </>
                      ) : (
                        <>
                          {/* Lower-tier save (name only) */}
                          <button type="submit" disabled={isSavingBranding} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-phosphor-400 text-space-900 text-sm font-medium hover:bg-phosphor-500 disabled:opacity-50 transition-colors" data-testid="settings-save-branding-btn">
                            {isSavingBranding ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />}
                            Save Name
                          </button>

                          {/* Upsell for logo + custom domains */}
                          <div className="p-5 rounded-xl bg-gradient-to-br from-violet-500/10 to-phosphor-500/10 border border-violet-400/20" data-testid="branding-upsell-card">
                            <div className="flex items-start gap-3">
                              <div className="w-9 h-9 rounded-lg bg-violet-400/10 flex items-center justify-center flex-shrink-0">
                                <Crown className="text-violet-400" size={18} />
                              </div>
                              <div className="flex-1">
                                <h3 className="text-sm font-medium text-white mb-1">Unlock Full Whitelabel</h3>
                                <p className="text-xs text-gray-400 mb-3">Upgrade to the <span className="text-violet-300 font-medium">Agency</span> or <span className="text-violet-300 font-medium">Pro</span> plan to add a custom logo and map your own domain (e.g. <span className="text-phosphor-400">app.youragency.com</span>).</p>
                                <ul className="text-xs text-gray-400 space-y-1 mb-4">
                                  <li className="flex items-center gap-2"><Check size={12} className="text-phosphor-400" /> Custom logo across the workspace</li>
                                  <li className="flex items-center gap-2"><Check size={12} className="text-phosphor-400" /> Custom login page on your own domain</li>
                                  <li className="flex items-center gap-2"><Check size={12} className="text-phosphor-400" /> Full client portal whitelabel</li>
                                </ul>
                                <a href={`/workspace/${currentWorkspaceId}/billing`} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-400 text-white text-sm font-medium hover:bg-violet-500 transition-colors" data-testid="branding-upgrade-btn">
                                  <CreditCard size={14} /> Upgrade Plan
                                </a>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </form>
                  )}

                  {/* Custom Domains — OUTSIDE outer form to avoid nested <form> hydration warning */}
                  {!isLoadingBranding && branding.whitelabel_enabled && (
                    <div className="mt-6">
                      <CustomDomainsSection api={api} workspaceId={currentWorkspaceId} />
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
